/**
 * LA VITRINA: los torneos abiertos de todos los clubes.
 *
 * QUÉ PREGUNTA CONTESTA, y es una que ninguna otra pantalla contestaba:
 * «¿dónde puedo jugar este mes?». Hasta acá los torneos solo se veían desde
 * adentro del club que los corre, así que para enterarse de uno había que ya
 * estar en ese club — o que alguien lo pasara por WhatsApp. Un jugador que
 * quiere jugar más no tiene cómo encontrar dónde.
 *
 * SOLO LOS QUE EL CLUB PUSO ACÁ. No es una lista de todo lo que existe: cada
 * club decide si su torneo se ve desde afuera. Hay clubes que arman torneos
 * internos y ponerlos en una vitrina sería publicar algo que nadie quiso
 * publicar.
 *
 * SIN LOS BORRADORES, y eso lo decide el servidor. Un borrador es un torneo
 * que todavía se está armando —le faltan categorías, la fecha puede cambiar—
 * y verlo acá hace que alguien se organice para ir a algo que no existe.
 *
 * NO REEMPLAZA A LOS DEL CLUB PROPIO, va debajo. Quien abre «Torneos» viene a
 * ver los suyos el 95 % de las veces; poner primero los de otros clubes sería
 * cambiarle la pantalla a todo el mundo por una minoría.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PublicTournamentCard } from '@tennisup/contracts';

import { Paginador, usePaginado } from './Paginado';
import { api, assetUrl } from '../lib/api';

export function OpenTournaments({
  /**
   * El club que NO se muestra: el que la persona ya está mirando.
   *
   * Sus torneos están arriba, en la lista de ese club, con más detalle y con
   * el botón de inscribirse. Repetirlos acá abajo hace dudar de si son los
   * mismos o hay dos torneos parecidos.
   */
  exceptCommunityId = null,
}: {
  exceptCommunityId?: string | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [torneos, setTorneos] = useState<PublicTournamentCard[] | null>(null);

  const load = useCallback(async () => {
    try {
      setTorneos(await api.publicTournaments());
    } catch {
      /*
       * SIN ALERTA Y SIN SECCIÓN. Esto es un extra al final de una pantalla
       * que funciona sin él; un cartel rojo de «no se pudieron cargar los
       * torneos de otros clubes» encima de la lista del club propio —que sí
       * cargó— hace creer que la pantalla está rota cuando no lo está.
       */
      setTorneos([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * `Array.isArray` Y NO `?? []`. Es la cuarta vez en esta aplicación que un
   * campo que el tipo promete como arreglo llega siendo otra cosa —los
   * patrocinadores fueron los primeros— y `?? []` no protege de eso: solo
   * atrapa el nulo. Un objeto pasa derecho y `.filter` no existe, así que
   * revienta el árbol entero de React y la pantalla desaparece.
   *
   * Un tipo es una promesa sobre el contrato, no una garantía sobre el cable.
   * Y ESTA sección es un extra al final de una pantalla que funciona sin
   * ella: que se lleve puesta la lista de torneos del club sería el peor
   * negocio posible.
   */
  const lista = Array.isArray(torneos) ? torneos : [];
  const otros = lista.filter((x) => x.communityId !== exceptCommunityId);
  const pagina = usePaginado(otros);

  // Mientras carga tampoco hay sección: un título con «Cargando…» debajo, al
  // final de una pantalla completa, es ruido que además parpadea.
  if (torneos === null || otros.length === 0) return null;

  return (
    <section className="open-tournaments">
      <h2>{t('tournaments.openTitle')}</h2>
      <p className="help">{t('tournaments.openHelp')}</p>

      <ul className="open-list">
        {pagina.visibles.map((torneo) => (
          <li key={torneo.id}>
            {/*
              A DÓNDE LLEVA DEPENDE DE SI SE PUEDE ENTRAR.
              
              Un torneo ABIERTO lleva a su ficha, que es donde está el botón
              de anotarse. Uno cerrado lleva al cuadro público: mirar es lo
              único que alguien de afuera puede hacer con él, y ofrecerle la
              ficha sería ofrecerle una pantalla llena de cosas que no puede
              tocar.

              ACÁ DECÍA QUE LA FICHA «pide permisos que quien mira no tiene»,
              y era falso: `GET /tournaments/:id` nunca miró la membresía. Ese
              comentario equivocado es lo que dejaba a la gente de afuera sin
              forma de llegar al botón de inscribirse — que es exactamente lo
              que Javier reportó con el Gato y el torneo de categoría A.
            */}
            <Link
              to={torneo.openToEveryone ? `/torneos/${torneo.id}` : `/cuadro/${torneo.id}`}
              className="open-card"
            >
              {torneo.bannerPath !== null && (
                <img
                  className="open-banner"
                  src={assetUrl(torneo.bannerPath) ?? ''}
                  alt=""
                  loading="lazy"
                />
              )}
              <span className="open-text">
                <strong>{torneo.name}</strong>
                <span className="help">
                  {torneo.communityName} · {fecha(torneo.startsAt)}
                </span>
                <span className="help">
                  {t('tournaments.entryCount', { n: torneo.entryCount })} ·{' '}
                  {t(`tournamentStatus.${torneo.status}`)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
    </section>
  );
}

/** `2026-10-11` → `11/10/2026`. Fecha civil, sin zona horaria. */
function fecha(iso: string): string {
  const [year, month, day] = iso.split('-');
  return year === undefined || month === undefined || day === undefined
    ? iso
    : `${day}/${month}/${year}`;
}
